import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Card } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { Timeline } from '../activities/Timeline.js';
import { LogActivityComposer } from '../activities/LogActivityComposer.js';
import { NotesPanel } from '../notes/NotesPanel.js';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel.js';
import { OpenTasksCard } from '../tasks/OpenTasksCard.js';
import { useContactQuery } from './api.js';

type Tab = 'overview' | 'activity' | 'tasks' | 'notes' | 'attachments';

/** docs/architecture screen "Contact — detail". design/orion-crm-design/
 * rendered-html/ContactDetail.html's header also shows "VIP"/"Renewal" tag
 * pills, a "Lead origin" callout ("Converted Aug 14 from lead"), and a
 * "Deals" tab with a real count — the tag pills need a tag-attach endpoint
 * that doesn't exist yet (same gap noted on the Contacts list), the lead-
 * origin callout is real and shown below since Contact.sourceLeadId already
 * exists, and the Deals tab is real (via the contact's linked
 * opportunities). "Email"/"Call" quick-action buttons in the header open
 * the same Log Activity composer already built for leads, pre-selecting
 * that kind, rather than being separate unwired buttons. */
export function ContactDetailPage() {
  const { contactId } = useParams({ from: '/_app/contacts/$contactId' });
  const { data: contact, isLoading, isError } = useContactQuery(contactId);
  const [tab, setTab] = useState<Tab>('overview');

  if (isLoading) return <PageHeader title="Loading…" />;
  if (isError || !contact) {
    return (
      <>
        <PageHeader title="Contact not found" />
        <main className="p-7 text-sm text-text-muted">We couldn't find that contact, or you don't have access to it.</main>
      </>
    );
  }

  const initials = `${contact.firstName[0] ?? ''}${contact.lastName[0] ?? ''}`.toUpperCase();

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Contacts', to: '/contacts' }, { label: contact.fullName }]} />
      <main className="flex flex-1 flex-col gap-3.5 overflow-hidden px-7 pb-6 pt-5">
        <Card className="flex shrink-0 items-center gap-4 p-5">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-avatar-indigo text-xl font-[650] text-white">{initials}</span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">{contact.fullName}</h1>
              <span className={`flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold ${contact.lifecycleStage === 'customer' ? 'bg-success-soft text-success' : 'bg-primary-soft text-primary-text'}`}>
                <span className={`size-1.5 rounded-full ${contact.lifecycleStage === 'customer' ? 'bg-success' : 'bg-primary'}`} />
                {contact.lifecycleStage === 'customer' ? 'Customer' : 'Lead'}
              </span>
              {contact.tags.map((t) => (
                <span key={t.id} className="flex h-5.5 items-center rounded-[6px] border border-border px-2 text-xs font-medium text-text-secondary">
                  {t.name}
                </span>
              ))}
            </div>
            <div className="text-sm leading-5 text-text-secondary">
              {contact.jobTitle ? `${contact.jobTitle} · ` : ''}
              <span className="font-semibold">{contact.primaryCompany?.name ?? 'No company'}</span>
              {contact.owner && <span className="text-text-muted"> · Owned by {contact.owner.displayName}</span>}
            </div>
          </div>
        </Card>

        <nav aria-label="Contact sections" className="flex h-9.5 shrink-0 items-end gap-6 border-b border-border">
          {(
            [
              ['overview', 'Overview'],
              ['activity', 'Activity'],
              ['tasks', 'Tasks'],
              ['notes', 'Notes'],
              ['attachments', 'Attachments'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={tab === key ? '-mb-px flex h-9.5 items-center border-b-2 border-primary text-sm font-semibold text-ink' : 'flex h-9.5 items-center text-sm font-medium text-text-muted hover:text-ink'}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === 'overview' && (
          <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr_260px] gap-4">
            <Card className="flex flex-col gap-2.5 overflow-y-auto p-4">
              <h2 className="text-[15px] font-semibold text-ink">Contact details</h2>
              <dl className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1.5 text-[13px] leading-4.5">
                <AboutRow label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : undefined} />
                <AboutRow label="Phone" value={contact.phone} />
                <AboutRow label="Mobile" value={contact.mobile} />
                <AboutRow label="Department" value={contact.department} />
                <AboutRow label="Owner" value={contact.owner?.displayName ?? null} />
                <AboutRow label="Created" value={new Date(contact.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
              </dl>
            </Card>
            <div className="flex min-h-0 flex-col gap-3">
              <LogActivityComposer contactId={contact.id} phone={contact.phone} />
              <Card className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
                <Timeline contactId={contact.id} />
              </Card>
            </div>
            <div className="flex min-h-0 flex-col gap-2.5 overflow-y-auto">
              <OpenTasksCard contactId={contact.id} />
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <Timeline contactId={contact.id} />
          </Card>
        )}
        {tab === 'tasks' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <OpenTasksCard contactId={contact.id} bare />
          </Card>
        )}
        {tab === 'notes' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <NotesPanel contactId={contact.id} />
          </Card>
        )}
        {tab === 'attachments' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <AttachmentsPanel contactId={contact.id} />
          </Card>
        )}
      </main>
    </>
  );
}

function AboutRow({ label, value, href }: { label: string; value: string | null | undefined; href?: string }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className="truncate text-ink">
        {value ? (href ? <a href={href} className="hover:underline">{value}</a> : value) : <span className="text-text-faint">—</span>}
      </dd>
    </>
  );
}
